import { useCallback, useEffect, useMemo, useState } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import BulkUploadModal from "../../../components/ui/BulkUploadModal";
import SuccessView from "../../../components/ui/SuccessView";
import { BulkUploadField } from "../../../lib/bulkUpload";
import { Brand } from "../../brand/@types";
import { useGetBrandsMutation } from "../../brand/brandApiSlice";
import { Partner } from "../../partner/@types";
import { useGetPartnersMutation } from "../../partner/partnerApiSlice";
import {
  useAddVehicleMutation,
  useGetVehicleTypesQuery,
} from "../vehicleApiSlice";

const VEHICLE_BULK_FIELDS: BulkUploadField[] = [
  {
    key: "vehicleName",
    label: "Vehicle name",
    required: true,
    defaultColumnName: "Vehicle name",
    example: "Asset name, Name",
  },
  {
    key: "vehicleModel",
    label: "Vehicle model",
    required: true,
    defaultColumnName: "Vehicle model",
    example: "Model, Make",
  },
  {
    key: "modelYear",
    label: "Model year",
    required: true,
    defaultColumnName: "Model year",
    example: "Year, Year of make",
  },
  {
    key: "datePurchased",
    label: "Date purchased",
    required: true,
    type: "date",
    defaultColumnName: "Date purchased",
    hint: "Use YYYY-MM-DD or a spreadsheet date.",
  },
  {
    key: "vehicleType",
    label: "Vehicle type",
    required: true,
    defaultColumnName: "Vehicle type",
    example: "Bus, Car, Truck",
  },
  {
    key: "licensePlate",
    label: "License plate",
    required: true,
    defaultColumnName: "License plate",
    example: "Plate number, Plate",
  },
  {
    key: "chassisNumber",
    label: "Registration number",
    required: true,
    defaultColumnName: "Registration number",
    example: "Chassis number, VIN",
  },
  {
    key: "brandName",
    label: "Brand",
    required: true,
    defaultColumnName: "Brand",
    hint: "Must match an existing brand name.",
  },
  {
    key: "incomeTarget",
    label: "Income target",
    required: true,
    type: "number",
    defaultColumnName: "Income target",
  },
  {
    key: "landingCost",
    label: "Landing cost",
    required: true,
    type: "number",
    defaultColumnName: "Landing cost",
  },
  {
    key: "partnerName",
    label: "Partner",
    required: false,
    defaultColumnName: "Partner",
    hint: "Leave blank, or use None / N/A, if the vehicle has no partner.",
  },
  {
    key: "maintenanceRule",
    label: "Maintenance rule",
    required: false,
    defaultColumnName: "Maintenance rule",
    hint: "Strict or Allow.",
  },
];

interface VehicleBulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const asList = <T,>(value: unknown): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (
    value &&
    typeof value === "object" &&
    Array.isArray((value as { data?: unknown }).data)
  ) {
    return (value as { data: T[] }).data;
  }
  return [];
};

const normalize = (value: string) => value.trim().toLowerCase();

const ABSENT_PARTNER_VALUES = new Set([
  "",
  "none",
  "null",
  "undefined",
  "nil",
  "n/a",
  "na",
  "n.a",
  "n.a.",
  "#n/a",
  "-",
  "--",
  "-- none --",
  "nothing",
  "empty",
  "blank",
  "no",
  "no partner",
  "no partners",
  "not applicable",
  "not assigned",
  "unassigned",
  "without partner",
  "without a partner",
  "no one",
]);

const isAbsentPartnerValue = (value: unknown): boolean => {
  if (value == null) return true;
  const normalized = String(value).trim().toLowerCase().replace(/\s+/g, " ");
  if (ABSENT_PARTNER_VALUES.has(normalized)) return true;
  const stripped = normalized.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
  return ABSENT_PARTNER_VALUES.has(stripped);
};

const VehicleBulkUploadModal: React.FC<VehicleBulkUploadModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);

  const [getBrands, { data: brandsResponse, isLoading: isBrandsLoading }] =
    useGetBrandsMutation();
  const [getPartners, { data: partnersResponse, isLoading: isPartnersLoading }] =
    useGetPartnersMutation();
  const { data: vehicleTypesResponse } = useGetVehicleTypesQuery(undefined, {
    skip: !isOpen,
  });
  const [addVehicle] = useAddVehicleMutation();

  useEffect(() => {
    if (!isOpen) return;
    getBrands({ pageNumber: 0, pageSize: 500 });
    getPartners({ pageNumber: 0, pageSize: 500 });
  }, [isOpen, getBrands, getPartners]);

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
      setUploadedCount(0);
    }
  }, [isOpen]);

  const brands = useMemo(
    () => asList<Brand>(brandsResponse?.data),
    [brandsResponse?.data],
  );
  const partners = useMemo(
    () =>
      asList<Partner>(partnersResponse?.data?.data ?? partnersResponse?.data),
    [partnersResponse?.data],
  );
  const vehicleTypes = useMemo(
    () => asList<string>(vehicleTypesResponse?.data),
    [vehicleTypesResponse?.data],
  );

  const resolveBrandId = useCallback(
    (value: string) => {
      const needle = normalize(value);
      const match = brands.find(
        (brand) =>
          normalize(brand.name) === needle || normalize(brand.id) === needle,
      );
      return match?.id;
    },
    [brands],
  );

  const resolvePartnerId = useCallback(
    (value: string) => {
      const needle = normalize(value);
      const match = partners.find(
        (partner) =>
          normalize(partner.fullName) === needle ||
          normalize(partner.id) === needle,
      );
      return match?.id;
    },
    [partners],
  );

  const resolveVehicleType = useCallback(
    (value: string) => {
      const needle = normalize(value);
      return vehicleTypes.find((type) => normalize(type) === needle);
    },
    [vehicleTypes],
  );

  const transformRecord = useCallback((record: Record<string, unknown>) => {
    if (isAbsentPartnerValue(record.partnerName)) {
      return { ...record, partnerName: "None" };
    }
    return record;
  }, []);

  const validateRecord = useCallback(
    (record: Record<string, unknown>) => {
      const errors: string[] = [];
      const brandName = String(record.brandName ?? "");
      if (!isBrandsLoading && brandName && !resolveBrandId(brandName)) {
        errors.push(`Brand "${brandName}" was not found`);
      }

      const partnerName = record.partnerName;
      if (
        !isPartnersLoading &&
        !isAbsentPartnerValue(partnerName) &&
        !resolvePartnerId(String(partnerName))
      ) {
        errors.push(`Partner "${String(partnerName)}" was not found`);
      }

      const vehicleType = String(record.vehicleType ?? "");
      if (
        vehicleTypes.length > 0 &&
        vehicleType &&
        !resolveVehicleType(vehicleType)
      ) {
        errors.push(`Unknown vehicle type "${vehicleType}"`);
      }

      const rule = String(record.maintenanceRule ?? "").trim();
      if (rule && !["strict", "allow"].includes(normalize(rule))) {
        errors.push("Maintenance rule must be Strict or Allow");
      }

      return errors;
    },
    [
      isBrandsLoading,
      isPartnersLoading,
      resolveBrandId,
      resolvePartnerId,
      resolveVehicleType,
      vehicleTypes.length,
    ],
  );

  const handleUploadRow = async (row: Record<string, unknown>) => {
    const rule = String(row.maintenanceRule ?? "").trim();
    const payload = {
      vehicleName: row.vehicleName,
      vehicleModel: row.vehicleModel,
      modelYear: String(row.modelYear ?? ""),
      datePurchased: row.datePurchased,
      vehicleType:
        resolveVehicleType(String(row.vehicleType ?? "")) || row.vehicleType,
      licensePlate: row.licensePlate,
      chassisNumber: row.chassisNumber,
      brandId: resolveBrandId(String(row.brandName ?? "")),
      incomeTarget: Number(row.incomeTarget),
      landingCost: Number(row.landingCost),
      partnerId: isAbsentPartnerValue(row.partnerName)
        ? undefined
        : resolvePartnerId(String(row.partnerName)),
      maintenanceRule: rule
        ? rule.charAt(0).toUpperCase() + rule.slice(1).toLowerCase()
        : "Allow",
    };

    try {
      await addVehicle(payload).unwrap();
    } catch (error) {
      const message =
        getErrorDetails(error).message || "Failed to add this vehicle.";
      throw new Error(message);
    }
  };

  const handleUploadComplete = (result: { succeeded: number; failed: number }) => {
    if (result.failed === 0 && result.succeeded > 0) {
      setUploadedCount(result.succeeded);
      setShowSuccess(true);
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    setUploadedCount(0);
    onClose();
  };

  if (showSuccess) {
    return (
      <BulkUploadSuccess
        isOpen={isOpen}
        count={uploadedCount}
        onClose={handleClose}
      />
    );
  }

  return (
    <BulkUploadModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Bulk upload vehicles"
      entityLabel="vehicles"
      fields={VEHICLE_BULK_FIELDS}
      storageKey="vehicles"
      templateFileName="vehicle-bulk-upload-template"
      onUploadRow={handleUploadRow}
      onUploadComplete={handleUploadComplete}
      getRowLabel={(row) =>
        [row.vehicleName, row.vehicleModel, row.licensePlate]
          .filter(Boolean)
          .join(" · ")
      }
      validateRecord={validateRecord}
      transformRecord={transformRecord}
    />
  );
};

const BulkUploadSuccess: React.FC<{
  isOpen: boolean;
  count: number;
  onClose: () => void;
}> = ({ isOpen, count, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed w-full h-full inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-lg shadow-xl max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full mx-4 overflow-hidden">
        <SuccessView
          handleCloseModal={onClose}
          heading=""
          message={`${count} ${
            count === 1 ? "vehicle has" : "vehicles have"
          } been uploaded successfully.`}
        />
      </div>
    </div>
  );
};

export default VehicleBulkUploadModal;
